import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { useSubscription } from "@/context/SubscriptionContext";
import { useSchool } from "@/context/SchoolContext";
import { createCreditCheckoutSession } from "@/utils/createCheckoutSession";
import { PageHeader } from "@/layouts/PageHeader";
import { PageContainer } from "@/layouts/PageContainer";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Sparkles, Building2 } from "lucide-react";

const REPORT_PACKS = [
  { id: "reports_5",   amount: 5 },
  { id: "reports_30",  amount: 30 },
  { id: "reports_60",  amount: 60 },
  { id: "reports_120", amount: 120 },
];

const AI_PACKS = [
  { id: "ai_25",  amount: 25 },
  { id: "ai_100", amount: 100 },
  { id: "ai_200", amount: 200 },
  { id: "ai_500", amount: 500 },
];

function CreditPackCard({ pack, onBuy, loading }) {
  return (
    <div className="border border-border rounded-xl p-4 bg-card flex flex-col gap-3 hover:border-primary/50 transition-colors">
      <p className="text-2xl font-semibold text-foreground">{pack.amount}</p>
      <p className="text-sm text-muted-foreground">credits</p>
      <Button
        size="sm"
        variant="outline"
        className="w-full mt-auto"
        disabled={loading === pack.id}
        onClick={() => onBuy(pack.id)}
      >
        {loading === pack.id ? "Redirecting..." : "Buy"}
      </Button>
    </div>
  );
}

export default function Usage() {
  const { getToken } = useAuth();
  const { reportsRemaining, aiCreditsRemaining, seatInfo, loading, error } = useSubscription();
  const { memberships, isAdmin } = useSchool();
  const [buyingPack, setBuyingPack] = useState(null);

  const handleBuy = async (packId) => {
    setBuyingPack(packId);
    try {
      const token = await getToken();
      await createCreditCheckoutSession(packId, token);
    } finally {
      setBuyingPack(null);
    }
  };

  const statusVariant =
    seatInfo?.status === "active" ? "success"
    : seatInfo?.status === "past_due" ? "warning"
    : "default";

  const statusLabel =
    seatInfo?.status === "active" ? "Active"
    : seatInfo?.status === "past_due" ? "Past Due"
    : seatInfo?.status === "cancelled" ? "Cancelled"
    : seatInfo?.status ?? "Unknown";

  const reportsUsedPct = seatInfo
    ? Math.min(100, Math.round((seatInfo.reportsUsed / seatInfo.reportsMax) * 100))
    : 0;
  const aiUsedPct = seatInfo
    ? Math.min(100, Math.round((seatInfo.aiGensUsed / seatInfo.aiMax) * 100))
    : 0;

  const schoolName = memberships.length === 1 ? memberships[0].name : null;

  return (
    <PageContainer>
      <PageHeader
        title="Credits & Billing"
        subtitle="Your quota, wallet balance, and credit purchases."
      />

      {loading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      )}
      {error && <p className="text-destructive">Failed to load subscription info.</p>}

      {!loading && !error && (
        <div className="space-y-10">

          {/* School Plan */}
          <div>
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-3">
              School Plan
            </h2>

            {seatInfo !== null ? (
              <div className="border border-border rounded-xl p-5 bg-card space-y-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={statusVariant}>{statusLabel}</Badge>
                    {schoolName && (
                      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <Building2 size={13} />
                        {schoolName}
                      </span>
                    )}
                  </div>
                  {seatInfo.periodEnd && (
                    <span className="text-xs text-muted-foreground">
                      Renews{" "}
                      {new Date(seatInfo.periodEnd).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  )}
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span>Reports Used</span>
                    <span
                      className={
                        seatInfo.reportsUsed >= seatInfo.reportsMax
                          ? "text-destructive font-semibold"
                          : ""
                      }
                    >
                      {seatInfo.reportsUsed} / {seatInfo.reportsMax}
                    </span>
                  </div>
                  <Progress value={reportsUsedPct} max={100} />
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span>AI Generations Used</span>
                    <span
                      className={
                        seatInfo.aiGensUsed >= seatInfo.aiMax
                          ? "text-destructive font-semibold"
                          : ""
                      }
                    >
                      {seatInfo.aiGensUsed} / {seatInfo.aiMax}
                    </span>
                  </div>
                  <Progress value={aiUsedPct} max={100} />
                </div>

                {isAdmin ? (
                  <p className="text-xs text-muted-foreground">
                    You&apos;re the school admin.{" "}
                    <Link to="/schools/seats" className="underline hover:text-foreground">
                      Manage seat billing
                    </Link>
                    .
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Managed by your school administrator.
                  </p>
                )}
              </div>
            ) : (
              <div className="border border-border rounded-xl p-5 bg-card">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                    <Building2 size={16} className="text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">No school plan</p>
                    <p className="text-sm text-muted-foreground">
                      School Plans cover 120 report credits and 200 AI credits per year. Your personal wallet below is always available if you need more.
                    </p>
                    {memberships.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        You are in a school but don&apos;t have a school plan assigned to you. Ask your admin.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Personal Wallet */}
          <div>
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-3">
              Personal Wallet
            </h2>
            {seatInfo !== null && (
              <p className="text-xs text-muted-foreground mb-3">
                Only drawn on after your school plan allocation runs out.
              </p>
            )}
            <div className="grid grid-cols-2 gap-4 max-[480px]:grid-cols-1">
              <div className="border border-border rounded-xl p-5 bg-card flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <FileText size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {reportsRemaining ?? "-"}
                  </p>
                  <p className="text-sm text-muted-foreground">Report Credits</p>
                </div>
              </div>
              <div className="border border-border rounded-xl p-5 bg-card flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Sparkles size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {aiCreditsRemaining ?? "-"}
                  </p>
                  <p className="text-sm text-muted-foreground">AI Credits</p>
                </div>
              </div>
            </div>
          </div>

          {/* Buy Credits */}
          <div>
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-3">
              Buy Credits
            </h2>
            <p className="text-xs text-muted-foreground mb-5">
              Credits are added to your personal wallet and never expire.
            </p>

            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <FileText size={14} /> Report Credits
              </h3>
              <div className="grid grid-cols-4 gap-3 max-[640px]:grid-cols-2">
                {REPORT_PACKS.map((pack) => (
                  <CreditPackCard
                    key={pack.id}
                    pack={pack}
                    onBuy={handleBuy}
                    loading={buyingPack}
                  />
                ))}
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Sparkles size={14} /> AI Credits
              </h3>
              <div className="grid grid-cols-4 gap-3 max-[640px]:grid-cols-2">
                {AI_PACKS.map((pack) => (
                  <CreditPackCard
                    key={pack.id}
                    pack={pack}
                    onBuy={handleBuy}
                    loading={buyingPack}
                  />
                ))}
              </div>
            </div>
          </div>

        </div>
      )}
    </PageContainer>
  );
}
